/**
 * Raw rig board source (ENG-946).
 *
 * A module singleton describing the control space of the CURRENTLY LOADED OGS
 * bundle — names, dimension, valid range, trained pose table, macros, coupling
 * — plus the one function that pushes a raw rig vector into the live splat.
 *
 * OGSModel registers it when a bundle's packs land (everything here comes from
 * that bundle: `rig_names.json`, `scene.json`, `rig_presets.npy`). The Rig
 * Preview panel reads it to build the board. A character with no OGS bundle
 * simply never registers, and the panel offers ARKit mode only — the raw board
 * is meaningless without a control space to address.
 *
 * The pose table is fetched LAZILY, the first time someone actually opens the
 * raw board: it is ~65 KB for eyeline and nobody who never opens the panel
 * should pay for it at boot.
 */
import { parseNpyFloat32 } from '../../lib/npyFloat32.js'
import { parseMouthController } from '../../lib/rigBoard.js'

const listeners = new Set()

export const rigBoardSource = {
  /** Bundle subject, for the panel header. */
  subject: null,
  /** MetaHuman CTRL_* names, in the bundle's own control order. */
  names: null,
  /** Control-space width (names.length). */
  dim: 0,
  /** Valid control domain declared by scene.json (`rig_range`). */
  range: [0, 1],
  /** Flat trained pose table (rows * dim), or null until loaded. */
  presets: null,
  /** Declared pose count (scene.json `num_poses`). */
  numPoses: 0,
  /** { mouth_open_pose, mouth_closed_pose, ... } — pose INDICES, not values. */
  macros: null,
  /** [{ driver, slopes: { idx: slope } }] */
  coupling: null,
  /** Base-conditioned mouth controller spec (scene.json `mouth_controller`), or
   *  null. Supersedes the global mouth macro for bundles that ship it; a bundle
   *  without one keeps the macro. See lib/rigBoard.js buildMouthController. */
  mouthController: null,
  /** Push a raw rig vector to the live splat. Set by OGSModel. */
  setRig: null,
  /**
   * The DNA's RAW control layer, when the exact MetaHuman rig is driving this
   * bundle: `{ names, controlSpace, guiToRaw }`.
   *
   * NULL on the rig2mesh path, which is still the default — that is a distilled
   * ONNX with no DNA behind it, so there is no second control layer to show and
   * the panel must say so rather than render an empty one. Also null on a
   * riglogic.wasm predating the raw exports.
   *
   * The naming here is a trap worth stating: this board's "raw rig" means THE
   * BUNDLE'S OWN control vector, as opposed to ARKit-52 — and that vector is
   * written in RigLogic's *GUI* layer. `rawLayer` is RigLogic's RAW layer, which
   * is a different thing. Hence the UI calls the board "bundle controls" and
   * this section "RigLogic RAW".
   */
  rawLayer: null,
  /** Lazy loader for the pose table. Set by OGSModel. */
  loadPresets: null,
}

export function subscribeRigBoard(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function notify() {
  for (const fn of listeners) {
    try { fn(rigBoardSource) } catch (e) { console.error('[rig-board] listener failed:', e) }
  }
}

/** The bundle's declared control domain, or the [0, 1] a MetaHuman board is.
 *
 *  ONE resolver, because both consumers of it clamp: the rig board's sliders and
 *  the live ARKit path's trained limits. They used to answer differently for a
 *  bundle declaring no `rig_range` — the board defaulted to [0, 1] and clamped to
 *  the trained box inside it, while the ARKit path read the raw field, got null,
 *  and clamped to NOTHING. That is the exact split trained limits exist to close:
 *  the same pose blowing up or not depending only on which UI produced it. */
export function resolveRigRange(scene) {
  const r = scene?.rig_range
  return Array.isArray(r) && r.length === 2 ? [Number(r[0]), Number(r[1])] : [0, 1]
}

/** The bundle's trained pose table, by filename.
 *
 *  `scene.rig_presets` is the top-level table the pose player reads. A bundle
 *  that ships none still has the HEAD branch's own `rig_presets`
 *  (`rig_params_head.npy`) — the same table, in the same control space (on
 *  eyeline-v10c the two files are identical). Falling back to it is what gives
 *  such a bundle trained poses at all: without one the pose player is empty, the
 *  deform anchors at the ZERO rig (an extreme corner of a [0, 1] board, not a
 *  neutral), and every trained-pose consumer — the ARKit base, trained limits,
 *  the subspace fit, the live mouth drive — goes dark. */
export function resolveRigPresetsFile(scene) {
  if (typeof scene?.rig_presets === 'string') return scene.rig_presets
  const head = scene?.branches?.head ?? scene?.branches?.[Object.keys(scene?.branches ?? {})[0]]
  return typeof head?.rig_presets === 'string' ? head.rig_presets : null
}

/**
 * Register the loaded bundle's control space.
 * @param {object} spec
 * @param {string[]} spec.names        rig_names.json
 * @param {object}   spec.scene        parsed scene.json (raw snake_case)
 * @param {(name:string)=>Promise<Uint8Array>} spec.fetchBytes  lazy bundle file reader
 * @param {(rig:Float32Array)=>void} spec.setRig
 */
export function registerRigBoard({ names, scene, fetchBytes, setRig }) {
  if (!Array.isArray(names) || names.length === 0) { clearRigBoard(); return }

  rigBoardSource.subject = scene?.subject ?? null
  rigBoardSource.names = names
  rigBoardSource.dim = names.length
  rigBoardSource.range = resolveRigRange(scene)
  rigBoardSource.numPoses = Number(scene?.num_poses) || 0
  rigBoardSource.macros = scene?.macros ?? null
  rigBoardSource.coupling = Array.isArray(scene?.coupling) ? scene.coupling : null
  rigBoardSource.mouthController = parseMouthController(scene?.mouth_controller)
  rigBoardSource.presets = null
  rigBoardSource.setRig = setRig ?? null
  // rawLayer is NOT set here: the board is registered before any branch is
  // built, so there is nothing to ask yet. setRigBoardRawLayer fills it in when
  // the pipeline resolves its head branch. Cleared, though — a re-register is a
  // new bundle, and the old rig's conversion would be another identity's.
  rigBoardSource.rawLayer = null

  const presetsFile = resolveRigPresetsFile(scene)
  rigBoardSource.loadPresets = (presetsFile && fetchBytes)
    ? async () => {
      if (rigBoardSource.presets) return rigBoardSource.presets
      const bytes = await fetchBytes(presetsFile)
      const { data, rows, cols } = parseNpyFloat32(bytes)
      if (cols !== rigBoardSource.dim) {
        // A pose table in a different control space would silently pose the
        // wrong controls — refuse it and keep the board slider-only.
        throw new Error(
          `${presetsFile}: ${cols} controls, but the bundle's rig is ${rigBoardSource.dim}`)
      }
      rigBoardSource.presets = data
      if (!rigBoardSource.numPoses) rigBoardSource.numPoses = rows
      notify()
      return data
    }
    : null

  console.info(
    `[rig-board] ${rigBoardSource.subject ?? 'bundle'}: ${rigBoardSource.dim} controls, `
    + `range [${rigBoardSource.range.join(', ')}], ${rigBoardSource.numPoses} trained poses`
    + `${rigBoardSource.coupling ? `, coupling on ${rigBoardSource.coupling.length} driver(s)` : ''}`
    + `${rigBoardSource.mouthController ? ', base-conditioned mouth controller' : ''}`)
  notify()
}

/** Publish (or withdraw) the DNA's RAW control layer once the pipeline knows
 *  which deform skins the head branch. Separate from registerRigBoard purely
 *  because of that ordering — see the note there. */
export function setRigBoardRawLayer(layer) {
  rigBoardSource.rawLayer = layer ?? null
  notify()
}

export function clearRigBoard() {
  rigBoardSource.subject = null
  rigBoardSource.names = null
  rigBoardSource.dim = 0
  rigBoardSource.range = [0, 1]
  rigBoardSource.presets = null
  rigBoardSource.numPoses = 0
  rigBoardSource.macros = null
  rigBoardSource.coupling = null
  rigBoardSource.mouthController = null
  rigBoardSource.setRig = null
  rigBoardSource.loadPresets = null
  // A stale rawLayer would hold the previous character's rig alive AND offer a
  // conversion against the wrong DNA — the readout would look plausible and be
  // another identity's.
  rigBoardSource.rawLayer = null
  notify()
}

if (typeof window !== 'undefined') {
  window.__RIG_BOARD__ = rigBoardSource
}
